/**
 * Error Boundary Component
 * Comprehensive error handling with security features
 */

import React from 'react';
import './ErrorBoundary.css';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error: error
    };
  }

  componentDidCatch(error, errorInfo) {
    // Generate unique error ID
    const errorId = this.generateErrorId();

    // Log error details
    this.logError(error, errorInfo, errorId);

    // Update state with error details
    this.setState({
      error: error,
      errorInfo: errorInfo,
      errorId: errorId
    });
  }

  generateErrorId = () => {
    return `ERR_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  logError = (error, errorInfo, errorId) => {
    // Create error log entry
    const errorLog = {
      errorId,
      timestamp: new Date().toISOString(),
      error: {
        message: error.message,
        stack: error.stack,
        name: error.name
      },
      errorInfo: {
        componentStack: errorInfo.componentStack
      },
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: this.getCurrentUserId()
    };


    // Error logged locally for debugging
  };

  getCurrentUserId = () => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      return user?.user?.id || 'anonymous';
    } catch {
      return 'anonymous';
    }
  };


  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    });
  };

  handleGoHome = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    });
    window.location.href = '/';
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          errorId={this.state.errorId}
          onRetry={this.handleRetry}
          onGoHome={this.handleGoHome}
          onReload={this.handleReload}
        />
      );
    }

    return this.props.children;
  }
}

// Error Fallback Component
const ErrorFallback = ({ error, errorInfo, errorId, onRetry, onGoHome, onReload }) => {
  const [showDetails, setShowDetails] = React.useState(false);

  const handleGoHome = () => {
    onGoHome();
    // Use window.location for navigation to avoid Router context issues
    window.location.href = '/';
  };

  return (
    <div className="error-boundary">
      <div className="error-container">
        <div className="error-icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
        </div>

        <div className="error-content">
          <h1>Something went wrong</h1>
          <p>We're sorry, but something unexpected happened. Our team has been notified.</p>

          {errorId && (
            <div className="error-id">
              <strong>Error ID:</strong> {errorId}
            </div>
          )}

          <div className="error-actions">
            <button className="error-button primary" onClick={onRetry}>
              Try Again
            </button>
            <button className="error-button secondary" onClick={handleGoHome}>
              Go Home
            </button>
            <button className="error-button secondary" onClick={onReload}>
              Reload Page
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

// Higher-order component for error boundaries
export const withErrorBoundary = (Component, errorBoundaryProps = {}) => {
  const WrappedComponent = (props) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;

  return WrappedComponent;
};

// Hook for error reporting
export const useErrorReporting = () => {
  const reportError = React.useCallback((error, errorInfo = null) => {
    const errorId = `ERR_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const errorLog = {
      errorId,
      timestamp: new Date().toISOString(),
      error: {
        message: error.message,
        stack: error.stack,
        name: error.name
      },
      errorInfo,
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: getCurrentUserId()
    };


    // Error logged locally

    return errorId;
  }, []);

  return { reportError };
};

const getCurrentUserId = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user'));
    return user?.user?.id || 'anonymous';
  } catch {
    return 'anonymous';
  }
};

export default ErrorBoundary;